import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { Autocomplete, Chip, CircularProgress, FormControl, Grid, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import FieldNames from '../../../defaults/FieldNames.json';
import {
  setProject,
  setProjectDiscoveryDates,
  setProjectFireCodes,
  setProjectIDs
} from '../../../redux/slices/filterSlice';
import ErrorService from '../../../services/ErrorService';
import ProjectService from '../../../services/ProjectService';

const BlueAlien = "#1976d2";

const useStyles = makeStyles({
  underline: {
    "&&&:before": {
      borderBottom: "2px solid red"
    },
    "&&:after": {
      borderBottom: "2px solid red"
    }
  },
  condensedFormControl: {
    margin: "5px 15px 5px 15px",
    minWidth: 120,
    width: '90%'
  },
  formControl: {
    margin: 15,
    minWidth: 120,
    width: '90%'
  },
  snackBarStyle: {
    top: '120px !important'
  },
});

const theme = createTheme({
  overrides: {
    MuiPaper: {
      root: {
        backgroundColor: 'green'
      }
    },
    MuiOutlinedInput: {
      root: {
        '& $notchedOutline': {
          //initial border
          borderColor: BlueAlien,
        },
        '&:hover $notchedOutline': {
          //border on hover
          borderColor: BlueAlien,
          border: '2px solid'
        },
        '&$focused $notchedOutline': {
          //border on focus
          borderColor: BlueAlien,
          border: '2px solid'
        },
      },
    }
  },
});

export default function AutoCompleteProjectLinkedFilter({ userInfo, cachedProjects, setCachedProjects, projectNameSelection, setProjectNameSelection,
   projectDiscoveryDateSelection, setProjectDiscoveryDateSelection, projectFireCodeSelection, setProjectFireCodeSelection, projectIDSelection, setProjectIDSelection }) {
  // Project Names
  const [projectNameOpen, setProjectNameOpen] = useState(false);
  const [projectNameOptions, setProjectNameOptions] = useState([]);
  const projectNameLoading = projectNameOpen && projectNameOptions.length === 0;
  // Project Discovery Dates
  const [showDiscoveryDateSelector, setShowDiscoveryDateSelector] = useState(false)
  const [discoveryDateOptionObjects, setDiscoveryDateOptionObjects] = useState([]);
  const [projectDiscoveryDateOpen, setProjectDiscoveryDateOpen] = useState(false);
  const [projectDiscoveryDateOptions, setProjectDiscoveryDateOptions] = useState([]);
  const projectDiscoveryDateLoading = projectDiscoveryDateOpen && projectDiscoveryDateOptions.length === 0;
  // Project Fire Code
  const [showProjectFireCodeSelector, setShowProjectFireCodeSelector] = useState(false);
  const [fireCodeOptionObjects, setFireCodeOptionObjects] = useState([]);
  const [projectFireCodeOpen, setProjectFireCodeOpen] = useState(false);
  const [projectFireCodeOptions, setProjectFireCodeOptions] = useState([]);
  const projectFireCodeLoading = projectFireCodeOpen && projectFireCodeOptions.length === 0;
  // Project IDs
  const [showProjectIDSelector, setShowProjectIDSelector] = useState(false);
  const [projectIDOpen, setProjectIDOpen] = useState(false);
  const [projectIDOptions, setProjectIDOptions] = useState([]);
  const projectIDLoading = projectIDOpen && projectIDOptions.length === 0;
  
  const classes = useStyles();
  const dispatch = useDispatch();

  const checkLoadCachedProjects = async () => {
    if(!cachedProjects?.length > 0)
    {
      return ProjectService.getAllProjects().then(response => {
        if(response?.length > 0){
          let filteredResponse = response.filter(x => x[FieldNames.projectFields.name]); // Filter nulls/undefined
          let mappedProjNames = filteredResponse.map(x => x[FieldNames.projectFields.name]);
          let countDictionary = mappedProjNames.reduce((prev, curr) => (prev[curr] = ++prev[curr] || 1, prev), {});
  
          const returnedProjects = filteredResponse.map(x => {
            let projectName = x[FieldNames.projectFields.name];
            let countOfName = countDictionary[projectName];
  
            let mappedObj = {
              ID: x[FieldNames.projectFields.id],
              Name: projectName, 
              MatchingNameCount: countOfName, 
              NameWithCount: countOfName > 1 ? `${projectName} (${countOfName})` : projectName,
              DiscoveryDate: x[FieldNames.projectFields.fireDiscoveryDate], 
              IsFirePerimeter: x[FieldNames.projectFields.isFirePerimeter],
              FireCode: x[FieldNames.projectFields.fireCode]
            }
            return mappedObj;
          });
  
          setCachedProjects(returnedProjects);
          return returnedProjects;
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
      })
    }
    else
    {
      return cachedProjects;
    }
  }

  useEffect(() => {
    let active = true;

    if (!projectNameLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        // Query and set cached projects
        let cachedProjectsValues = await checkLoadCachedProjects();
        const projectNameCountMapping = [...new Set(cachedProjectsValues.map(x => x["NameWithCount"]))];
        setProjectNameOptions(projectNameCountMapping);
      }
    })();

    return () => {
      active = false;
    };
  }, [projectNameLoading]);

  useEffect(() => {
    if (!projectNameOpen) {
      const projectNameCountMapping = [...new Set(cachedProjects.map(x => x["NameWithCount"]))];
      setProjectNameOptions(projectNameCountMapping);    }
  }, [projectNameOpen]);

  useEffect(() => {
    let active = true;

    (async () => {
      if (active) {
        if (projectNameSelection?.length > 0) {
          // Query and set cached projects
          let cachedProjectsValues = await checkLoadCachedProjects();
          let matchingProjects = cachedProjectsValues.filter(x => projectNameSelection.some(y => y === x[FieldNames.projectFields.name]));
          if(projectNameSelection.length > 1 || matchingProjects?.length > 1)
          {
            // Multiple selected projects, or multiple projects that match on name
            setShowDiscoveryDateSelector(true);
            setShowProjectIDSelector(true);
            setShowProjectFireCodeSelector(true);
          }
          else
          {
            setShowDiscoveryDateSelector(false);
            setShowProjectIDSelector(false);
            setShowProjectFireCodeSelector(false);
          }
        }
        else
        {
          setShowDiscoveryDateSelector(false);
          setShowProjectIDSelector(false);
          setShowProjectFireCodeSelector(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [projectNameSelection]);

  useEffect(() => {
    let active = true;

    if (!projectDiscoveryDateOpen) {
      return undefined;
    }

    (async () => {
      if (active) {
        // Query and set cached projects
        let cachedProjectsValues = await checkLoadCachedProjects();
        const nameFilteredProjects = cachedProjectsValues?.filter(x => projectNameSelection.some(selectedName => selectedName === x[FieldNames.projectFields.name]))
        const mappedProjectDates = nameFilteredProjects?.map(x => x[FieldNames.projectFields.fireDiscoveryDate]).filter(x => x);
        let countDictionary = mappedProjectDates.reduce((prev, curr) => (prev[curr] = ++prev[curr] || 1, prev), {});
        let discoveryDateObjects = mappedProjectDates.map(x => {
          let date = x;
          let countOfDate = countDictionary[x];

          let mappedObj = {
            Date: date, 
            MatchingDateCount: countOfDate, 
            DateWithCount: countOfDate > 1 ? `${date} (${countOfDate})` : date,
          }
          return mappedObj;
        });
        const projectDateCountMapping = [...new Set(discoveryDateObjects.map(x => x["DateWithCount"]))];
        setDiscoveryDateOptionObjects(discoveryDateObjects)
        setProjectDiscoveryDateOptions(projectDateCountMapping);
        if(projectDateCountMapping.length === 0)
        {
          setProjectDiscoveryDateOpen(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [projectDiscoveryDateLoading]);

  useEffect(() => {
    if (!projectDiscoveryDateOpen) {
      setProjectDiscoveryDateOptions([]);
    }
  }, [projectDiscoveryDateOpen]);

  useEffect(() => {
    let active = true;

    if (!projectFireCodeOpen) {
      return undefined;
    }

    (async () => {
      if (active) {
        // Query and set cached projects
        let cachedProjectsValues = await checkLoadCachedProjects();
        let nameDateFilteredProjects = cachedProjectsValues?.filter(x => projectNameSelection.some(selectedName => selectedName === x[FieldNames.projectFields.name]))
        if(projectDiscoveryDateSelection?.length > 0)
        {
          nameDateFilteredProjects = nameDateFilteredProjects?.filter(x => 
            projectDiscoveryDateSelection.some(selectedDate => selectedDate && moment(selectedDate).isSame(x[FieldNames.projectFields.fireDiscoveryDate], 'day'))
          );
        }
        const mappedProjectCodes = nameDateFilteredProjects?.map(x => x[FieldNames.projectFields.fireCode]).filter(x => x);
        let countDictionary = mappedProjectCodes.reduce((prev, curr) => (prev[curr] = ++prev[curr] || 1, prev), {});
        let fireCodeObjects = mappedProjectCodes.map(x => {
          let code = x;
          let countOfCode = countDictionary[x];

          let mappedObj = {
            FireCode: code, 
            MatchingCodeCount: countOfCode, 
            CodeWithCount: countOfCode > 1 ? `${code} (${countOfCode})` : code,
          }
          return mappedObj;
        });
        const projectCodeCountMapping = [...new Set(fireCodeObjects.map(x => x["CodeWithCount"]))] ?? [];
        setFireCodeOptionObjects(fireCodeObjects)
        setProjectFireCodeOptions(projectCodeCountMapping);
        if(projectCodeCountMapping.length === 0)
        {
          setProjectFireCodeOpen(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [projectFireCodeLoading]);

  useEffect(() => {
    if (!projectFireCodeOpen) {
      setProjectFireCodeOptions([]);
    }
  }, [projectFireCodeOpen]);

  useEffect(() => {
    let active = true;

    if (!projectIDLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        // Query and set cached projects
        let cachedProjectsValues = await checkLoadCachedProjects();
        let filteredProjects = cachedProjectsValues?.filter(x => 
                                            projectNameSelection.some(selectedName => selectedName === x[FieldNames.projectFields.name]))
        if(projectDiscoveryDateSelection?.length > 0)
        {
          filteredProjects = filteredProjects?.filter(x => 
            projectDiscoveryDateSelection.some(selectedDate => selectedDate && moment(selectedDate).isSame(x[FieldNames.projectFields.fireDiscoveryDate], 'day'))
          );
        }
        if(projectFireCodeSelection?.length > 0)
        {
          filteredProjects = filteredProjects?.filter(x => 
            projectFireCodeSelection.some(selectedCode => selectedCode === x[FieldNames.projectFields.fireCode])
          );
        }
        const mappedProjectIds = filteredProjects?.map(x => x[FieldNames.projectFields.id]);
        setProjectIDOptions(mappedProjectIds);
        if(mappedProjectIds.length === 0)
        {
          setProjectIDOpen(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [projectIDLoading]);

  useEffect(() => {
    if (!projectIDOpen) {
      setProjectIDOptions([]);
    }
  }, [projectIDOpen]);

  const clearDiscoveryDateInvalidValues = (projectNames) => {
    let tempDateArr = [];
    let projectsFilteredByNames = cachedProjects.filter(x => projectNames.some(y => (y === x["NameWithCount"]) || (y === x[FieldNames.projectFields.name])))
    let cachedProjectDates = projectsFilteredByNames.map(x => x[FieldNames.projectFields.fireDiscoveryDate]);
    projectDiscoveryDateSelection.forEach(x => {
      if(cachedProjectDates.includes(x))
      {
        tempDateArr.push(x);
      }
    })
    if(tempDateArr.length !== projectDiscoveryDateSelection.length)
    {
      dispatch(setProjectDiscoveryDates(tempDateArr));
      setProjectDiscoveryDateSelection(tempDateArr);
    }
  }

  const clearProjectFireCodeInvalidValues = (projectNames, projectDiscoveryDates) => {
    let tempCodeArr = [];
    let projectsFilteredByNames = cachedProjects?.filter(x => projectNames.some(y => (y === x["NameWithCount"]) || (y === x[FieldNames.projectFields.name])))
    let projectsFilteredByDates = projectsFilteredByNames?.filter(x => projectDiscoveryDates.some(y => y && moment(y).isSame(moment(x[FieldNames.projectFields.fireDiscoveryDate], 'day'))));
    let cachedProjectCodes = projectsFilteredByDates?.map(x => x[FieldNames.projectFields.fireCode]);
    projectFireCodeSelection.forEach(x => {
      if(cachedProjectCodes.includes(x))
      {
        tempCodeArr.push(x);
      }
    })
    if(tempCodeArr.length !== projectFireCodeSelection.length)
    {
      dispatch(setProjectFireCodes(tempCodeArr));
      setProjectFireCodeSelection(tempCodeArr);
    }
  }

  const clearProjectIDInvalidValues = (projectNames, projectDiscoveryDates, projectFireCodes) => {
    let tempIDArr = [];
    let filteredProjects = cachedProjects?.filter(x => projectNames.some(y => (y === x["NameWithCount"]) || (y === x[FieldNames.projectFields.name])))
    if(projectDiscoveryDates?.length > 0)
    {
      filteredProjects = filteredProjects?.filter(x => projectDiscoveryDates.some(y => y === x[FieldNames.projectFields.fireDiscoveryDate]));
    }
    if(projectFireCodes?.length > 0)
    {
      filteredProjects = filteredProjects?.filter(x => projectFireCodes.some(y => y === x[FieldNames.projectFields.fireCode]));
    }
    let cachedProjectIds = filteredProjects?.map(x => x[FieldNames.projectFields.id]);
    projectIDSelection.forEach(x => {
      if(cachedProjectIds.includes(x))
      {
        tempIDArr.push(x);
      }
    })
    if(tempIDArr.length !== projectIDSelection.length)
    {
      dispatch(setProjectIDs(tempIDArr));
      setProjectIDSelection(tempIDArr);
    }
  }

  return (
    <Grid container>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Project Name</label>
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                multiple
                id="projectNameSelect"
                value={projectNameSelection}
                disableClearable
                onChange={(event, newValue) => {
                  let matchingProjects = cachedProjects.filter(x => newValue.some(y => (y === x["NameWithCount"]) || (y === x[FieldNames.projectFields.name])));
                  let matchingProjectNames = matchingProjects.map(x => x[FieldNames.projectFields.name]);
                  let uniqueProjectNames = [...new Set(matchingProjectNames)];
                  dispatch(setProject(uniqueProjectNames));
                  setProjectNameSelection(uniqueProjectNames);
                  // Account for changes to project name selections with current discovery dates
                  clearDiscoveryDateInvalidValues(newValue);
                  // Account for changes to project name selections with current fire codes
                  clearProjectFireCodeInvalidValues(newValue, projectDiscoveryDateSelection);
                  // Account for changes to project name selections with current IDs
                  clearProjectIDInvalidValues(newValue, projectDiscoveryDateSelection, projectFireCodeSelection);
                }}
                onOpen={() => {
                  setProjectNameOpen(true);
                }}
                onClose={() => {
                  setProjectNameOpen(false);
                }}
                isOptionEqualToValue={(option, value) => 
                {
                  return option === value
                }}
                getOptionLabel={(option) => { 
                  return option 
                }}
                options={projectNameOptions}
                getOptionDisabled={(option) => {
                  if(projectNameSelection){
                    const selected = projectNameSelection;
                    if(option && selected?.includes(option)){
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }}
                loading={projectNameLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      disabled={false}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {projectNameLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      {
        !showDiscoveryDateSelector ? null :
        <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Discovery Date</label>
              </span>
            </Grid>
            <Grid item size={6}>
            <Autocomplete
              multiple
                id="projectDiscoveryDateSelect"
                value={projectDiscoveryDateSelection}
                disableClearable
                onChange={(event, newValue) => {
                  let matchingDateObjs = discoveryDateOptionObjects.filter(x => newValue.some(y => y === x["DateWithCount"]));
                  let matchingDiscoveryDates = matchingDateObjs.map(x => x["Date"]);
                  let uniqueProjectDates = [...new Set(matchingDiscoveryDates)];
                  dispatch(setProjectDiscoveryDates(uniqueProjectDates));
                  setProjectDiscoveryDateSelection(uniqueProjectDates);
                  // Account for changes to project name selections with current fire codes
                  clearProjectFireCodeInvalidValues(projectNameSelection, newValue);
                  // Account for changes to project name/discovery date selections
                  clearProjectIDInvalidValues(projectNameSelection, newValue, projectFireCodeSelection);
                }}
                onOpen={() => {
                  setProjectDiscoveryDateOpen(true);
                }}
                onClose={() => {
                  setProjectDiscoveryDateOpen(false);
                }}
                isOptionEqualToValue={(option, value) => option === value}
                getOptionLabel={(option) => { 
                  return option 
                }}
                options={projectDiscoveryDateOptions}
                getOptionDisabled={(option) => {
                  if(projectDiscoveryDateSelection){
                    const selected = projectDiscoveryDateSelection;
                    if(option && selected?.includes(option)){
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }}
                loading={projectDiscoveryDateLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      disabled={false}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {projectDiscoveryDateLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      }
      {
        !showProjectFireCodeSelector ? null :
        <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Fire Codes</label>
              </span>
            </Grid>
            <Grid item size={6}>
            <Autocomplete
              multiple
                id="projectFireCodeeSelect"
                value={projectFireCodeSelection}
                disableClearable
                onChange={(event, newValue) => {
                  let matchingCodeObjs = fireCodeOptionObjects.filter(x => newValue.some(y => y === x["CodeWithCount"]));
                  let matchingFireCodes = matchingCodeObjs.map(x => x["FireCode"]);
                  let uniqueProjectFireCodes = [...new Set(matchingFireCodes)];
                  dispatch(setProjectFireCodes(uniqueProjectFireCodes));
                  setProjectFireCodeSelection(uniqueProjectFireCodes);
                  // Account for changes to project name/discovery date selections
                  clearProjectIDInvalidValues(projectNameSelection, projectDiscoveryDateSelection, newValue);
                }}
                onOpen={() => {
                  setProjectFireCodeOpen(true);
                }}
                onClose={() => {
                  setProjectFireCodeOpen(false);
                }}
                isOptionEqualToValue={(option, value) => option === value}
                getOptionLabel={(option) => { 
                  return option 
                }}
                options={projectFireCodeOptions}
                getOptionDisabled={(option) => {
                  if(projectFireCodeSelection){
                    const selected = projectFireCodeSelection;
                    if(option && selected?.includes(option)){
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }}
                loading={projectFireCodeLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      disabled={false}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {projectFireCodeLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      }
      {
        !showProjectIDSelector ? null :
        <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Project ID</label>
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                multiple
                id="ProjectIDSelect"
                open={projectIDOpen}
                value={projectIDSelection}
                disableClearable
                onChange={(event, newValue) => {
                  dispatch(setProjectIDs(newValue));
                  setProjectIDSelection(newValue);
                }}
                onOpen={() => {
                  setProjectIDOpen(true);
                }}
                onClose={() => {
                  setProjectIDOpen(false);
                }}
                isOptionEqualToValue={(option, value) => { return option === value }}
                getOptionLabel={(option) => { 
                  return option 
                }}
                options={projectIDOptions}
                getOptionDisabled={(option) => {
                  if(projectIDSelection){
                    const selected = projectIDSelection;
                    if(option && selected?.includes(option)){
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }}
                loading={projectIDLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      disabled={false}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {projectIDLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      }
    </Grid>
  );
}